import React from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import { Tooltip } from '@mui/material';

const BillTable = ({ bills, onView, onPrint, onDownloadPDF, onEdit, onDelete }) => {
    // Helper to get Status Color
    const getStatusType = (status) => {
        if (!status) return 'default';
        return status.toLowerCase();
    };

    const getModeType = (mode) => {
        if (!mode) return 'default';
        return mode.toLowerCase().replace(' ', '-');
    };

    return (
        <div className="table-responsive">
            <div className="history-table-wrapper">
                <table className="history-table">
                    <thead>
                        <tr>
                            <th>Bill #</th>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Phone</th>
                            <th style={{ textAlign: 'center' }}>Mode</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bills.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                                    No bills found matching your filters.
                                </td>
                            </tr>
                        ) : (
                            bills.map(bill => (
                                <tr key={bill._id}>
                                    <td>
                                        <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            #{bill.billNumber}
                                            <span className={`badge ${bill.source === 'LEGACY' ? 'legacy' : 'system'}`}>
                                                {bill.source === 'LEGACY' ? 'Legacy' : 'Digital'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        {new Date(bill.billDate || bill.createdAt).toLocaleDateString()}
                                        <div style={{ fontSize: '10px', color: '#999' }}>{new Date(bill.createdAt).toLocaleTimeString()}</div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: '500' }}>{bill.customerName || bill.customer?.firstName + ' ' + bill.customer?.lastName}</div>
                                        {bill.customer?.customId && <span style={{ fontSize: '10px', background: '#eee', padding: '2px 4px', borderRadius: '3px' }}>{bill.customer.customId}</span>}
                                    </td>
                                    <td>{bill.customerPhone || bill.customer?.phone || '-'}</td>

                                    <td style={{ textAlign: 'center' }}>
                                        {bill.paymentMode || bill.paymentDetails?.mode ? (
                                            <span className={`badge ${getModeType(bill.paymentMode || bill.paymentDetails?.mode)}`}>
                                                {bill.paymentMode || bill.paymentDetails?.mode}
                                            </span>
                                        ) : (
                                            <span style={{ color: '#aaa', fontSize: '11px' }}>—</span>
                                        )}
                                    </td>

                                    <td style={{ textAlign: 'center' }}>
                                        <span className={`badge ${getStatusType(bill.paymentStatus || bill.paymentDetails?.status)}`}>
                                            {bill.paymentStatus || bill.paymentDetails?.status || 'Pending'}
                                        </span>
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: '700' }}>₹{(bill.amount || bill.totalAmount).toLocaleString()}</div>
                                        {/* Show Balance for Partial/Pending */}
                                        {((bill.source === 'LEGACY' ? bill.paymentStatus : bill.paymentDetails?.status) === 'Partial' ||
                                            (bill.source === 'LEGACY' ? bill.paymentStatus : bill.paymentDetails?.status) === 'Pending') && (
                                                <div style={{ fontSize: '11px', color: '#d32f2f', fontWeight: '600' }}>
                                                    Bal: ₹{(() => {
                                                        // Calculate Balance Logic
                                                        if (bill.source === 'LEGACY') {
                                                            const total = parseFloat(bill.amount || 0);
                                                            const paid = parseFloat(bill.paidAmount || 0);
                                                            return Math.max(0, total - paid).toLocaleString();
                                                        } else {
                                                            // System
                                                            if (bill.paymentDetails?.balance !== undefined) {
                                                                return parseFloat(bill.paymentDetails.balance).toLocaleString();
                                                            }
                                                            // Fallback
                                                            const total = parseFloat(bill.totalAmount || 0);
                                                            const paid = parseFloat(bill.paymentDetails?.advance || 0);
                                                            return Math.max(0, total - paid).toLocaleString();
                                                        }
                                                    })()}
                                                </div>
                                            )}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            {/* View */}
                                            <Tooltip title="View Bill" arrow>
                                                <button className="btn-icon" onClick={() => onView(bill)} style={{ color: '#1976d2' }}>
                                                    <VisibilityOutlinedIcon fontSize="small" />
                                                </button>
                                            </Tooltip>

                                            {/* Edit Payment (For All) */}
                                            <Tooltip title="Edit Payment" arrow>
                                                <button className="btn-icon" onClick={() => onEdit(bill)} style={{ color: '#f57c00' }}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </button>
                                            </Tooltip>

                                            {/* PDF (Digital Only) */}
                                            {bill.source !== 'LEGACY' && (
                                                <Tooltip title="Download PDF" arrow>
                                                    <button className="btn-icon" onClick={() => onDownloadPDF(bill)} style={{ color: '#d32f2f' }}>
                                                        <PictureAsPdfOutlinedIcon fontSize="small" />
                                                    </button>
                                                </Tooltip>
                                            )}

                                            {/* Delete Action */}
                                            <Tooltip title={bill.isDeleted ? "Restore" : "Delete Bill"} arrow>
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => onDelete(bill)}
                                                    style={{ color: bill.isDeleted ? '#2e7d32' : '#d32f2f', marginLeft: '4px' }}
                                                >
                                                    {bill.isDeleted ? (
                                                        <RestoreFromTrashOutlinedIcon fontSize="small" />
                                                    ) : (
                                                        <DeleteOutlinedIcon fontSize="small" />
                                                    )}
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BillTable;
